import type { ReactNode } from "react";
import { Animal } from "@/components/Animal";

export type ModeId = "cast" | "chaos" | "telephone";

const BAR = "speech-bar h-2 rounded-full bg-[#12162c]/45";

/** Staggered delays make the scene take turns instead of twitching in unison. */
const beat = (index: number) => ({ animationDelay: `${index * 0.14}s` });

type ModeInfo = {
  id: ModeId;
  name: string;
  blurb: string;
  tone: string;
  scene: ReactNode;
};

/**
 * The three games, drawn before they are described: one voice owning a whole
 * character, lines scattered across everyone, a chain where each ear hears
 * only the previous beak.
 *
 * Shared by the front door and the lobby's picker, so the card a player
 * recognised outside is the one the host points at inside. Inside a `group`
 * the scenes come alive on hover — critters hop, speech bars talk, relay
 * arrows push the message down the chain (see globals.css).
 */
export const MODES: readonly ModeInfo[] = [
  {
    id: "cast",
    name: "Каст",
    blurb: "Один игрок — один персонаж",
    tone: "bg-sun",
    scene: (
      <div className="flex w-full items-center justify-center gap-3">
        <span className="critter shrink-0" style={beat(0)}>
          <Animal id="cat" className="h-12 w-12" />
        </span>
        <div className="flex w-24 shrink-0 flex-col gap-1.5">
          <div className={`${BAR} w-full`} style={beat(0)} />
          <div className={`${BAR} w-3/4`} style={beat(1)} />
          <div className={`${BAR} w-5/6`} style={beat(2)} />
        </div>
      </div>
    ),
  },
  {
    id: "chaos",
    name: "Хаос",
    blurb: "Реплики вразнобой, голоса скачут",
    tone: "bg-berry",
    scene: (
      <div className="flex w-full items-end justify-center gap-4">
        {(["frog", "owl", "pig"] as const).map((id, index) => (
          <div key={id} className="flex flex-col items-center gap-1.5">
            <span className="critter" style={beat(index)}>
              <Animal id={id} className="h-10 w-10" />
            </span>
            {/* Mirrored widths: the widest bar in the middle keeps the row
                symmetric while still reading as different-length lines. */}
            <div className={`${BAR} ${["w-8", "w-12", "w-8"][index]}`} style={beat(index)} />
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "telephone",
    name: "Телефон",
    blurb: "Каждый слышит только предыдущего",
    tone: "bg-sky",
    scene: (
      <div className="flex w-full items-center justify-center gap-2">
        {(["parrot", "bunny", "panda"] as const).map((id, index) => (
          <span key={id} className="contents">
            {index > 0 && (
              <span
                className="relay-arrow font-display text-xl font-extrabold text-[#12162c]/60"
                style={beat(index - 1)}
                aria-hidden
              >
                →
              </span>
            )}
            <span className="critter" style={beat(index)}>
              <Animal id={id} className="h-10 w-10" />
            </span>
          </span>
        ))}
      </div>
    ),
  },
];

/** RU names for phases that echo the mode back, e.g. the round header. */
export const MODE_NAMES: Record<string, string> = Object.fromEntries(
  MODES.map((mode) => [mode.id, mode.name]),
);

/**
 * The little stage the scene stands on — fixed height so rows of cards align.
 *
 * Compact stages shrink the whole troupe with a transform inside a clipped
 * frame: the scenes are drawn for a ~9rem stage, and a narrow picker column
 * must scale the performance, not crop it.
 */
export function ModeStage({ scene, compact = false }: { scene: ReactNode; compact?: boolean }) {
  if (compact) {
    return (
      <div
        className="flex h-12 items-center justify-center overflow-hidden rounded-xl bg-[#fdfdff]/35"
        aria-hidden
      >
        <div className="w-48 shrink-0 scale-[0.52]">{scene}</div>
      </div>
    );
  }

  return (
    <div className="flex h-24 items-center rounded-2xl bg-[#fdfdff]/35 px-3" aria-hidden>
      {scene}
    </div>
  );
}
